import { Box, Chip, Tooltip } from '@mui/material';
import { Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

type Props = {
  operativeSummary?: Record<string, unknown> | null;
};

const OperativeSummary = ({ operativeSummary }: Props) => {
  if (!operativeSummary) return null;

  const booleanEntries = Object.entries(operativeSummary).filter(
    ([, value]) => typeof value === 'boolean',
  ) as [string, boolean][];

  if (booleanEntries.length === 0) return null;

  const values = booleanEntries.map(([, value]) => value);
  const allTrue = values.every(Boolean);
  const allFalse = values.every((v) => !v);

  const green = BASE_COLORS.green;
  const red = BASE_COLORS.red;
  const yellow = BASE_COLORS.yellow;

  let chipColor = yellow;
  if (allTrue) chipColor = green;
  else if (allFalse) chipColor = red;

  let chipLabel = 'Incompleto';
  if (allTrue) chipLabel = 'Completo';
  else if (allFalse) chipLabel = 'Pendiente';

  const renderTooltipContent = () => (
    <Stack
      direction="column"
      gap={0.75}
      bgcolor="white"
      p={1.25}
      borderRadius={2}
      border={1}
      borderColor="divider"
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'text.secondary',
          letterSpacing: 0.4,
        }}
      >
        Resumen operativo
      </Typography>
      {booleanEntries.map(([key, value]) => (
        <Stack
          key={key}
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            py: 0.4,
            px: 0.8,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'grey.50',
            },
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              bgcolor: alpha(value ? green : red, 0.16),
              border: '1px solid',
              borderColor: alpha(value ? green : red, 0.6),
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              color: 'text.primary',
              fontWeight: 500,
              letterSpacing: 0.1,
            }}
          >
            {key}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              ml: 'auto',
              fontSize: 11,
              fontWeight: 600,
              color: value ? green : red,
              textTransform: 'uppercase',
            }}
          >
            {value ? 'Listo' : 'Pendiente'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Tooltip
      placement="top"
      enterDelay={150}
      title={renderTooltipContent()}
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: 'transparent',
            padding: 0,
            boxShadow: 'none',
            border: 'none',
          },
        },
      }}
    >
      <Chip
        label={chipLabel}
        style={{
          backgroundColor: alpha(chipColor, 0.2),
          color: chipColor,
          fontWeight: 'bold',
          fontSize: '12px',
          opacity: 1,
          width: '110px',
        }}
      />
    </Tooltip>
  );
};

export default OperativeSummary;
